import { changeOwnPasswordSchema, MODULES } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { type FormEvent, useState } from 'react';
import { Badge, Button, Dl, ErrorBox, Field, Input, PageHeader, Spinner } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useUser } from '@/lib/auth';
import { formatMoment } from '@/lib/format';
import { notificationsQuery } from '@/lib/queries';

export function NotificationsPage() {
  const qc = useQueryClient();
  const q = useQuery(notificationsQuery);
  const readAll = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const readOne = useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const hasUnread = q.data?.some((n) => !n.readAt);

  return (
    <div className="max-w-[760px]">
      <PageHeader
        title="Уведомления"
        actions={
          hasUnread && (
            <Button variant="secondary" loading={readAll.isPending} onClick={() => readAll.mutate()}>
              Отметить все прочитанными
            </Button>
          )
        }
      />
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : !q.data.length ? (
        <div className="panel px-6 py-10 text-center text-steel">
          Уведомлений пока нет. Здесь появятся назначения, комментарии и согласования.
        </div>
      ) : (
        <ul className="panel divide-y divide-line">
          {q.data.map((n) => (
            <li key={n.id} className={clsx('flex gap-3 px-5 py-3.5', !n.readAt && 'bg-signal-soft/40')}>
              <span
                aria-hidden
                className={clsx(
                  'mt-2 size-2 shrink-0 rounded-full',
                  n.readAt ? 'bg-transparent' : 'bg-signal',
                )}
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{n.title}</p>
                {n.body && <p className="text-ink-soft">{n.body}</p>}
                <p className="mt-0.5 text-[12.5px] text-steel">{formatMoment(n.createdAt)}</p>
              </div>
              <div className="flex shrink-0 items-start gap-2">
                {n.link && (
                  <Link
                    to={n.link}
                    onClick={() => !n.readAt && readOne.mutate(n.id)}
                    className="link text-[13px]"
                  >
                    Открыть
                  </Link>
                )}
                {!n.readAt && (
                  <button
                    type="button"
                    className="text-[13px] text-steel hover:text-ink"
                    onClick={() => readOne.mutate(n.id)}
                  >
                    Прочитано
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ProfilePage() {
  const user = useUser();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const change = useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api('/auth/password', { method: 'POST', body }),
    onSuccess: () => setDone(true),
    onError: (err) =>
      err instanceof ApiError && setErrors(Object.keys(err.fields).length ? err.fields : { _: err.message }),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setDone(false);
    const fd = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    if (fd.newPassword !== fd.repeat) {
      setErrors({ repeat: 'Пароли не совпадают' });
      return;
    }
    const parsed = changeOwnPasswordSchema.safeParse(fd);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    change.mutate(parsed.data);
    e.currentTarget.reset();
  }

  return (
    <div className="max-w-[760px]">
      <PageHeader title="Профиль" />
      <section className="panel mb-6 px-5 py-5">
        <Dl
          items={[
            ['Сотрудник', user.employee?.fullName ?? '—'],
            ['Должность', user.employee?.positionName],
            ['Отдел', user.employee?.departmentName],
            ['Логин', user.login],
            [
              'Роли',
              <span key="roles" className="flex flex-wrap gap-1.5">
                {user.roles.map((r) => (
                  <Badge key={`${r.code}-${r.scopeDepartmentId ?? 'all'}`}>{r.name}</Badge>
                ))}
              </span>,
            ],
          ]}
        />
      </section>

      <section className="panel">
        <h2 className="border-b border-line px-5 py-3.5 font-semibold">Смена пароля</h2>
        <form onSubmit={onSubmit} noValidate className="flex max-w-[360px] flex-col gap-4 px-5 py-5">
          <Field label="Текущий пароль" error={errors.currentPassword}>
            {(p) => <Input {...p} name="currentPassword" type="password" autoComplete="current-password" />}
          </Field>
          <Field label="Новый пароль" error={errors.newPassword} hint="Не короче 8 символов">
            {(p) => <Input {...p} name="newPassword" type="password" autoComplete="new-password" />}
          </Field>
          <Field label="Новый пароль ещё раз" error={errors.repeat}>
            {(p) => <Input {...p} name="repeat" type="password" autoComplete="new-password" />}
          </Field>
          {errors._ && (
            <p role="alert" className="text-[13px] text-danger">
              {errors._}
            </p>
          )}
          {done && (
            <p role="status" className="text-[13px] text-ok">
              Пароль изменён. На других устройствах нужно войти заново.
            </p>
          )}
          <Button type="submit" loading={change.isPending} className="self-start">
            Сменить пароль
          </Button>
        </form>
      </section>
    </div>
  );
}

export function PlannedModulePage() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const mod = MODULES.find((m) => pathname.startsWith(m.path));
  if (!mod) return <NotFoundPage />;
  return (
    <div className="max-w-[640px]">
      <PageHeader title={mod.title} description={mod.description} />
      <div className="panel px-6 py-6">
        <p className="font-medium">Раздел появится в спринте {mod.sprint}</p>
        <p className="mt-1 text-steel">
          До запуска продолжаем работать с этим разделом в Битрикс24. Данные перенесём при переключении —
          вводить заново ничего не придётся.
        </p>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="max-w-[640px]">
      <PageHeader title="Страница не найдена" description="Ссылка устарела или в адресе опечатка." />
      <Link to="/" className="link">
        На главную
      </Link>
    </div>
  );
}
